import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  Plus,
  Eye,
  Pencil,
  Check,
  XCircle,
  Calendar,
  Download,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreVertical,
} from 'lucide-react';
import materialRequisitionService from '../services/materialRequisitionService';
import authService from '../services/authService';
import Layout from '../components/Layout';

const MaterialRequisitionListPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [filters, setFilters] = useState({
    requisition_number: '',
    work_order_number: '',
    status: 'all',
    start_date: '',
    end_date: ''
  });

  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    action: '', 
    requisition: null, 
    reason: '' 
  });

  // Check if user can approve/reject (admin or warehouse_manager)
  const canApprove = user && ['admin', 'warehouse_manager'].includes(user.role);

  const loadRequisitions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Add filters if they have values
      if (filters.requisition_number) {
        params.requisition_number = filters.requisition_number;
      }
      if (filters.work_order_number) {
        params.work_order_number = filters.work_order_number;
      }
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const response = await materialRequisitionService.getRequisitions(params);

      if (response.requisitions && Array.isArray(response.requisitions)) {
        setRequisitions(response.requisitions);
      } else if (Array.isArray(response)) {
        setRequisitions(response);
      } else {
        console.error('Unexpected response structure:', response);
        setRequisitions([]);
      }

      // Update pagination info from response
      if (response.pagination) {
        setTotalCount(response.pagination.total_count || 0);
        setTotalPages(response.pagination.total_pages || 1);
      }
    } catch (err) {
      setError(err.response?.data?.error || '加载领料申请列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, sortBy, sortOrder]);

  useEffect(() => {
    loadRequisitions();
  }, [loadRequisitions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value === 'all' ? '' : value
    }));
  };

  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(1); // Reset to first page when filter changes
    loadRequisitions();
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      requisition_number: '',
      work_order_number: '',
      status: 'all',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const handleSortChange = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleAction = (action, requisition) => {
    if (action === 'view') {
      navigate(`/material-requisitions/${requisition._id}/view`);
    } else if (action === 'edit') {
      navigate(`/material-requisitions/${requisition._id}/edit`);
    } else if (action === 'create') {
      navigate('/material-requisitions/new');
    } else if (['approve', 'reject', 'cancel'].includes(action)) {
      setConfirmDialog({ open: true, action, requisition, reason: '' });
    }
  };

  const handleConfirmAction = async () => {
    const { action, requisition, reason } = confirmDialog;
    setConfirmDialog({ open: false, action: '', requisition: null, reason: '' });

    try {
      if (action === 'approve') {
        await materialRequisitionService.approveRequisition(requisition._id);
        setRequisitions(prev => prev.map(r =>
          r._id === requisition._id ? { ...r, status: 'approved' } : r
        ));
        setError('');
        setTimeout(() => alert('领料申请已批准'), 100);
      } else if (action === 'reject') {
        await materialRequisitionService.rejectRequisition(requisition._id, reason);
        setRequisitions(prev => prev.map(r =>
          r._id === requisition._id ? { ...r, status: 'rejected' } : r
        ));
        setError('');
        setTimeout(() => alert('领料申请已拒绝'), 100);
      } else if (action === 'cancel') {
        await materialRequisitionService.cancelRequisition(requisition._id);
        setRequisitions(prev => prev.map(r =>
          r._id === requisition._id ? { ...r, status: 'cancelled' } : r
        ));
        setError('');
        setTimeout(() => alert('领料申请已取消'), 100);
      }
    } catch (err) {
      setError(err.response?.data?.error || `操作失败：${action}`);
    }
  };

  const handleExport = async () => {
    try {
      const exportFilters = { ...filters };
      if (exportFilters.status === 'all') {
        delete exportFilters.status;
      }
      
      const blob = await materialRequisitionService.exportRequisitions(exportFilters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `material_requisitions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || '导出失败');
    }
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'default';
      case 'pending_approval':
        return 'secondary';
      case 'rejected':
        return 'destructive';
      case 'cancelled':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending_approval':
        return '待审批';
      case 'approved':
        return '已通过';
      case 'rejected':
        return '已拒绝';
      case 'cancelled':
        return '已取消';
      default:
        return status || '未知';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ columnId }) => {
    if (sortBy !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  const getDialogTitle = () => {
    switch (confirmDialog.action) {
      case 'approve':
        return '批准领料申请';
      case 'reject':
        return '拒绝领料申请';
      case 'cancel':
        return '取消领料申请';
      default:
        return '';
    }
  };

  const getDialogDescription = () => {
    const requisitionNumber = confirmDialog.requisition?.requisition_number || '';
    switch (confirmDialog.action) {
      case 'approve':
        return `确定要批准领料申请 ${requisitionNumber} 吗？`;
      case 'reject':
        return `确定要拒绝领料申请 ${requisitionNumber} 吗？`;
      case 'cancel':
        return `确定要取消领料申请 ${requisitionNumber} 吗？此操作不可撤销。`;
      default:
        return '';
    }
  };

  const content = (
    <>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Header with Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">领料申请列表</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={loading}
          >
            <Download className="mr-2 h-4 w-4" />
            导出
          </Button>
          <Button onClick={() => handleAction('create', null)}>
            <Plus className="mr-2 h-4 w-4" />
            新建申请
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-3 items-end">
            {/* Requisition Number Filter */}
            <div className="flex-1 min-w-[180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  name="requisition_number"
                  placeholder="按申请号筛选..."
                  value={filters.requisition_number}
                  onChange={handleFilterChange}
                  className="pl-10 pr-8"
                />
                {filters.requisition_number && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => handleClearFilter('requisition_number')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Work Order Number Filter */}
            <div className="flex-1 min-w-[180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  name="work_order_number"
                  placeholder="按工单号筛选..."
                  value={filters.work_order_number}
                  onChange={handleFilterChange}
                  className="pl-10 pr-8"
                />
                {filters.work_order_number && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => handleClearFilter('work_order_number')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">状态</Label>
              <Select value={filters.status} onValueChange={(v) => handleSelectFilterChange('status', v)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="pending_approval">待审批</SelectItem>
                  <SelectItem value="approved">已通过</SelectItem>
                  <SelectItem value="rejected">已拒绝</SelectItem>
                  <SelectItem value="cancelled">已取消</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="h-8 w-px bg-border mx-1" />

            {/* Date Range Filters */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div className="space-y-1">
                <Label className="text-xs">开始日期</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  className="h-9 w-[140px]"
                />
              </div>
              <span className="text-sm text-muted-foreground">至</span>
              <div className="space-y-1">
                <Label className="text-xs">结束日期</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  className="h-9 w-[140px]"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <Button
              type="submit"
              variant="ghost"
              size="icon"
              disabled={loading}
              title="刷新"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClearAllFilters}
              disabled={loading}
              title="清除所有筛选"
            >
              <X className="h-4 w-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Requisitions Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        <button
                          className="flex items-center hover:text-primary transition-colors"
                          onClick={() => handleSortChange('requisition_number')}
                        >
                          申请号
                          <SortIcon columnId="requisition_number" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        <button
                          className="flex items-center hover:text-primary transition-colors"
                          onClick={() => handleSortChange('work_order_number')}
                        >
                          工单号
                          <SortIcon columnId="work_order_number" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        关联工单
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        状态
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        申请人
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        <button
                          className="flex items-center hover:text-primary transition-colors"
                          onClick={() => handleSortChange('created_at')}
                        >
                          创建时间
                          <SortIcon columnId="created_at" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {requisitions.map((requisition) => (
                      <tr key={requisition._id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <button
                            className="text-primary underline hover:text-primary/80 transition-colors font-medium"
                            onClick={() => handleAction('view', requisition)}
                          >
                            {requisition.requisition_number}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          {requisition.is_multi_work_order && requisition.work_orders?.length > 0 ? (
                            <div className="space-y-1">
                              <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
                                {requisition.work_orders.length} 个工单
                              </Badge>
                              <div className="flex flex-wrap gap-1">
                                {requisition.work_orders.slice(0, 2).map((wo, idx) => (
                                  <Link
                                    key={idx}
                                    to={`/production/${wo.production_order_id}`}
                                    className="text-xs text-primary underline hover:text-primary/80"
                                  >
                                    {wo.production_order_number}
                                  </Link>
                                ))}
                                {requisition.work_orders.length > 2 && (
                                  <span className="text-xs text-muted-foreground">...</span>
                                )}
                              </div>
                            </div>
                          ) : requisition.production_order_id ? (
                            <Link
                              to={`/production/${requisition.production_order_id}`}
                              className="text-primary underline hover:text-primary/80 transition-colors"
                            >
                              {requisition.work_order_number || '-'}
                            </Link>
                          ) : requisition.subcontract_order_id ? (
                            <Link
                              to={`/subcontract/${requisition.subcontract_order_number || requisition.subcontract_order_id}`}
                              className="text-primary underline hover:text-primary/80 transition-colors"
                            >
                              <span className="inline-flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">[外协]</span>
                                {requisition.subcontract_order_number || requisition.subcontract_order_id}
                              </span>
                            </Link>
                          ) : (
                            requisition.work_order_number || '-'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusVariant(requisition.status)}>
                            {getStatusLabel(requisition.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {requisition.applicant_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(requisition.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleAction('view', requisition)}>
                                <Eye className="mr-2 h-4 w-4" />
                                查看详情
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAction('edit', requisition)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                编辑
                              </DropdownMenuItem>
                              
                              {/* Approval actions - only for admin/warehouse_manager */}
                              {canApprove && requisition.status === 'pending_approval' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleAction('approve', requisition)}>
                                    <Check className="mr-2 h-4 w-4" />
                                    批准
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAction('reject', requisition)}>
                                    <XCircle className="mr-2 h-4 w-4" />
                                    拒绝
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {/* Cancel action - for pending_approval status */}
                              {requisition.status === 'pending_approval' && (
                                <DropdownMenuItem
                                  onClick={() => handleAction('cancel', requisition)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <XCircle className="mr-2 h-4 w-4" />
                                  取消申请
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}

                    {requisitions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          未找到领料申请
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center py-4 border-t">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                    >
                      上一页
                    </Button>
                    <span className="text-sm text-muted-foreground px-4">
                      第 {page} / {totalPages} 页
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                    >
                      下一页
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <p className="mt-4 text-sm text-muted-foreground">
        显示 {requisitions.length} / {totalCount} 条领料申请
      </p>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', requisition: null, reason: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>{getDialogDescription()}</DialogDescription>
          </DialogHeader>
          {confirmDialog.action === 'reject' && (
            <div className="py-4">
              <Label htmlFor="reject-reason">拒绝原因</Label>
              <Textarea
                id="reject-reason"
                placeholder="请输入拒绝原因..."
                value={confirmDialog.reason}
                onChange={(e) => setConfirmDialog(prev => ({ ...prev, reason: e.target.value }))}
                className="mt-2"
                rows={3}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, action: '', requisition: null, reason: '' })}
            >
              取消
            </Button>
            <Button
              variant={confirmDialog.action === 'reject' || confirmDialog.action === 'cancel' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
            >
              {confirmDialog.action === 'approve' && '批准'}
              {confirmDialog.action === 'reject' && '拒绝'}
              {confirmDialog.action === 'cancel' && '取消'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout title="领料申请" activeTab="material-requisitions">
      {content}
    </Layout>
  );
};


export default MaterialRequisitionListPage;
